import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useOrder } from '@/hooks/useOrders';
import {
  useUpdateOrderStatus,
  useUpdateTracking,
  useCancelOrder,
  useUpdateProduction,
  useUpdatePricing,
} from '@/hooks/useAdmin';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { HorizontalScrollIndicator } from '@/components/ui/HorizontalScrollIndicator';

import { HeroBar } from '@/components/admin/order-detail/HeroBar';
import { TerminalBanner } from '@/components/admin/order-detail/TerminalBanner';
import { StatusStepper } from '@/components/admin/order-detail/StatusStepper';
import { PrintJobCard } from '@/components/admin/order-detail/PrintJobCard';
import { ProductionCard } from '@/components/admin/order-detail/ProductionCard';
import { ShippingTrackingCard } from '@/components/admin/order-detail/ShippingTrackingCard';
import { AddressCards } from '@/components/admin/order-detail/AddressCards';
import { PricingCard } from '@/components/admin/order-detail/PricingCard';
import { CostProfitCard } from '@/components/admin/order-detail/CostProfitCard';
import { AdminNotesCard, CustomerNotesCard } from '@/components/admin/order-detail/AdminNotesCard';
import { LegalReviewSection } from '@/components/admin/order-detail/LegalReviewSection';
import { ActivityLogSection } from '@/components/admin/order-detail/ActivityLogSection';
import { DataExportCard } from '@/components/admin/order-detail/DataExportCard';
import { CancelRejectModal } from '@/components/admin/order-detail/CancelRejectModal';
import { FailureModal } from '@/components/admin/order-detail/FailureModal';

import {
  TABS,
  DEFAULT_TAB_BY_STATUS,
  type TabId,
} from '@/components/admin/order-detail/constants';

// Status-aware card highlighting — which card gets emphasis per status
const HIGHLIGHTED_CARDS: Record<string, string> = {
  queued: 'printJob',
  printing: 'production',
  post_processing: 'production',
  packed: 'shipping',
  shipped: 'shipping',
};

export const AdminOrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading, error } = useOrder(id ?? null);
  const updateStatus = useUpdateOrderStatus();
  const updateTracking = useUpdateTracking();
  const cancelOrder = useCancelOrder();
  const updateProduction = useUpdateProduction();
  const updatePricing = useUpdatePricing();
  const toast = useToast();

  // Tab state — initialized from order status
  const [activeTab, setActiveTab] = useState<TabId>('job');
  const [hoveredTab, setHoveredTab] = useState<TabId | null>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);

  // Modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);

  // Set default tab based on order status
  useEffect(() => {
    if (order) {
      const defaultTab = DEFAULT_TAB_BY_STATUS[order.status] ?? 'job';
      setActiveTab(defaultTab);
    }
  }, [order?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't intercept when typing in inputs
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === '1') setActiveTab('job');
      if (e.key === '2') setActiveTab('fulfill');
      if (e.key === '3') setActiveTab('finance');
      if (e.key === '4') setActiveTab('admin');
      if (e.key === '5') setActiveTab('status');
      if (e.key === 'Escape') {
        setShowCancelModal(false);
        setShowFailureModal(false);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleAdvance = useCallback(
    (nextStatus: string) => {
      if (!order) return;
      updateStatus.mutate(
        { orderId: order.orderId, status: nextStatus },
        {
          onSuccess: () => {
            toast.success(`Status updated to ${nextStatus.replace(/_/g, ' ')}`);
            if (nextStatus === 'shipped') setActiveTab('fulfill');
          },
          onError: () => toast.error('Failed to update status'),
        },
      );
    },
    [order, updateStatus, toast],
  );

  const handleCancelConfirm = useCallback(
    (action: 'cancel' | 'reject', reason: string) => {
      if (!order) return;
      cancelOrder.mutate(
        { orderId: order.orderId, action, reason },
        {
          onSuccess: () => {
            setShowCancelModal(false);
            toast.success(`Order ${action === 'cancel' ? 'cancelled' : 'rejected'}`);
          },
          onError: () => toast.error(`Failed to ${action} order`),
        },
      );
    },
    [order, cancelOrder, toast],
  );

  const handleFailureConfirm = useCallback(
    (category: string, notes: string) => {
      if (!order) return;
      updateStatus.mutate(
        { orderId: order.orderId, status: 'failed' },
        {
          onSuccess: () => {
            setShowFailureModal(false);
            toast.success('Order marked as failed');
          },
          onError: () => toast.error('Failed to update order'),
        },
      );
      updateProduction.mutate({
        orderId: order.orderId,
        data: {
          production: {
            ...(order.production ?? {}),
            printSuccess: false,
            failureCategory: category,
            failureNotes: notes,
            reprintCount: (order.production?.reprintCount ?? 0) + 1,
          },
        },
      });
    },
    [order, updateStatus, updateProduction, toast],
  );

  const handleSaveTracking = useCallback(
    (trackingNumber: string, carrier: string) => {
      if (!order) return;
      updateTracking.mutate(
        {
          orderId: order.orderId,
          trackingNumber,
          trackingCarrier: carrier,
        },
        {
          onSuccess: () => toast.success('Tracking saved'),
          onError: () => toast.error('Failed to save tracking'),
        },
      );
    },
    [order, updateTracking, toast],
  );

  const handleSaveProduction = useCallback(
    (data: Record<string, unknown>) => {
      if (!order) return;
      updateProduction.mutate(
        { orderId: order.orderId, data },
        {
          onSuccess: () => toast.success('Production data saved'),
          onError: () => toast.error('Failed to save production data'),
        },
      );
    },
    [order, updateProduction, toast],
  );

  const handleSavePricing = useCallback(
    (data: Record<string, unknown>) => {
      if (!order) return;
      updatePricing.mutate(
        { orderId: order.orderId, data },
        {
          onSuccess: () => toast.success('Costs updated'),
          onError: () => toast.error('Failed to update costs'),
        },
      );
    },
    [order, updatePricing, toast],
  );

  const handleSaveNotes = useCallback(
    (notes: string | null) => {
      if (!order) return;
      updateProduction.mutate({
        orderId: order.orderId,
        data: { adminNotes: notes },
      });
    },
    [order, updateProduction],
  );

  const handleSaveLegal = useCallback(
    (data: Record<string, unknown>) => {
      if (!order) return;
      updateProduction.mutate(
        { orderId: order.orderId, data },
        {
          onSuccess: () => toast.success('Legal review updated'),
          onError: () => toast.error('Failed to update legal review'),
        },
      );
    },
    [order, updateProduction, toast],
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (error || !order) {
    return (
      <div style={{ padding: 'var(--space-8)' }}>
        <h2 style={{ fontSize: 'var(--text-xl)', color: 'var(--text-primary)' }}>
          Order Not Found
        </h2>
        <Link
          to="/admin/orders"
          style={{
            color: 'var(--text-accent)',
            textDecoration: 'none',
            marginTop: 'var(--space-4)',
            display: 'inline-block',
          }}
        >
          Back to Orders
        </Link>
      </div>
    );
  }

  // Check for legal flags (for tab badge)
  const legalFlagCount = [
    order.legal?.restrictedContentFlag,
    order.legal?.copyrightConcernFlag,
    order.legal?.safetyConcernFlag,
  ].filter(Boolean).length;

  // Check for missing tracking (for tab badge)
  const needsTracking =
    ['packed', 'shipped', 'delivered'].includes(order.status) &&
    !order.shipping?.trackingNumber &&
    !order.trackingNumber;

  // Which card to highlight based on status
  const highlighted = HIGHLIGHTED_CARDS[order.status] ?? null;

  // Derive error message from mutations
  const advanceErrorMsg = updateStatus.error
    ? (updateStatus.error as Error).message ?? 'Failed to update status'
    : null;

  return (
    <div style={{ maxWidth: '960px', fontSize: '14px' }}>
      {/* Back link */}
      <Link
        to="/admin/orders"
        style={{
          fontSize: '12px',
          color: 'var(--text-accent)',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          marginBottom: '12px',
        }}
      >
        &larr; Back to orders
      </Link>

      {/* Hero */}
      <HeroBar order={order} />

      {/* Terminal banner */}
      <TerminalBanner order={order} />

      {/* Main two-column layout */}
      <div className="admin-order-layout">
        {/* LEFT COLUMN: Tabs + content */}
        <div style={{ minWidth: 0 }}>
          {/* Tab bar */}
          <div
            ref={tabBarRef}
            className="hide-native-scrollbar"
            style={{
              display: 'flex',
              gap: 0,
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const isHovered = hoveredTab === tab.id;

              return (
                <button
                  key={tab.id}
                  className={tab.mobileOnly ? 'mobile-only-tab' : undefined}
                  onClick={() => setActiveTab(tab.id)}
                  onMouseEnter={() => setHoveredTab(tab.id)}
                  onMouseLeave={() => setHoveredTab(null)}
                  style={{
                    padding: '10px 14px',
                    fontSize: '13px',
                    color: isActive
                      ? 'var(--text-primary)'
                      : isHovered
                        ? 'var(--text-secondary)'
                        : 'var(--text-tertiary)',
                    fontWeight: isActive ? 'var(--font-weight-medium)' : 'var(--font-weight-regular)',
                    cursor: 'pointer',
                    background: 'none',
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderBottom: `2px solid ${isActive ? 'var(--text-primary)' : 'transparent'}`,
                    transition: 'color 150ms, border-color 150ms',
                    position: 'relative',
                    minHeight: '44px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {tab.label}
                  {/* Badge dot for fulfillment */}
                  {tab.id === 'fulfill' && needsTracking && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '6px',
                        right: '4px',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-primary-500)',
                      }}
                    />
                  )}
                  {/* Badge dot for admin */}
                  {tab.id === 'admin' && legalFlagCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '6px',
                        right: '4px',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-error-500)',
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <HorizontalScrollIndicator scrollRef={tabBarRef} />
          </div>

          {/* Tab content */}
          {activeTab === 'job' && (
            <div>
              <PrintJobCard order={order} highlighted={highlighted === 'printJob'} />
              <ProductionCard
                order={order}
                onSave={handleSaveProduction}
                isPending={updateProduction.isPending}
                highlighted={highlighted === 'production'}
              />
            </div>
          )}

          {activeTab === 'fulfill' && (
            <div>
              <ShippingTrackingCard
                order={order}
                onSave={handleSaveTracking}
                isPending={updateTracking.isPending}
                highlighted={highlighted === 'shipping'}
              />
              <AddressCards
                shipping={order.shippingAddress}
                billing={order.billingAddress}
              />
            </div>
          )}

          {activeTab === 'finance' && (
            <div>
              <PricingCard pricing={order.pricing} />
              <CostProfitCard
                pricing={order.pricing}
                onSave={handleSavePricing}
                isPending={updatePricing.isPending}
              />
            </div>
          )}

          {activeTab === 'admin' && (
            <div>
              <AdminNotesCard
                order={order}
                onSave={handleSaveNotes}
                isPending={updateProduction.isPending}
              />
              <CustomerNotesCard notes={order.customerNotes} />
              <LegalReviewSection
                order={order}
                onSave={handleSaveLegal}
                isPending={updateProduction.isPending}
              />
              <ActivityLogSection orderId={order.orderId} />
              <DataExportCard orderId={order.orderId} />
            </div>
          )}

          {activeTab === 'status' && (
            <div>
              <StatusStepper
                order={order}
                onAdvance={handleAdvance}
                onMarkFailed={() => setShowFailureModal(true)}
                onCancelReject={() => setShowCancelModal(true)}
                isAdvancing={updateStatus.isPending}
                advanceError={advanceErrorMsg}
              />
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Status stepper (desktop only) */}
        <div className="desktop-only-status" style={{ position: 'sticky', top: 'calc(var(--header-height) + var(--space-4))', alignSelf: 'start' }}>
          <StatusStepper
            order={order}
            onAdvance={handleAdvance}
            onMarkFailed={() => setShowFailureModal(true)}
            onCancelReject={() => setShowCancelModal(true)}
            isAdvancing={updateStatus.isPending}
            advanceError={advanceErrorMsg}
          />
        </div>
      </div>

      {/* Modals */}
      {showCancelModal && (
        <CancelRejectModal
          orderId={order.orderId}
          onConfirm={handleCancelConfirm}
          onClose={() => setShowCancelModal(false)}
          isPending={cancelOrder.isPending}
        />
      )}

      {showFailureModal && (
        <FailureModal
          onConfirm={handleFailureConfirm}
          onClose={() => setShowFailureModal(false)}
          isPending={updateStatus.isPending}
        />
      )}
    </div>
  );
};
